import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Paper, Typography, TextField, CircularProgress } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import { Tooltip } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import {
  ApiGet,
  ApiPost,
  ApiPut,
  ApiDelete,
} from "../helpers/API/ApiData";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";
import moment from "moment";

import { DataTable, TableColumnDef } from "../components/DataTable";
import { SelfHostedEditor } from "../components/TextEditor";
import { PageHeader } from "../components/PageHeader";
import { LanguageTabs } from "../components/LanguageTabs";
import { usePermissions } from "../hooks/usePermissions";
import { useContentLanguage } from "../hooks";

import { createSanitizedMarkup } from "../utils/createSanitizedMarkup";

interface EditorState {
  content: string;
  content_ger: string;
}

export default function Imprint() {
  const { canCreate, canEdit, canDelete } = usePermissions("/imprint");
  const [lang, setLang] = useContentLanguage();
  const [imprintData, setImprintData] = useState<any>({});
  const [addImprint, setAddimprint] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllImprint, setGetAllImprint] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [imprintIdForDelete, setImprintIdForDelete] = useState<any>();
  const [imprintIdForEdit, setImprintIdForEdit] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);

  const [editorState, setEditorState] = useState<EditorState>({
    content: "",
    content_ger: "",
  });

  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  useEffect(() => {
    getAllImprintData();
  }, [page, countPerPage, search]);

  //for close add imprite model
  const addImpreintModelClose = (e: any) => {
    setAddimprint(false);
    setIsEditApi(false);
    setErrors({});
    setImprintData({});
    setEditorState({ content: "", content_ger: "" });
  };
  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setImprintData({ ...imprintData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  //form validation
  const formValidation = () => {
    let formIsValid = true;
    const errors: any = {};
    if (!imprintData?.imprintName_ger?.trim()) {
      formIsValid = false;
      errors["imprintName_ger"] = "*Bitte geben Sie den Titel ein (DE)";
    }
    if (!imprintData?.imprintName?.trim()) {
      formIsValid = false;
      errors["imprintName"] = "*Bitte geben Sie den Titel ein (EN)";
    }
    if (!editorState?.content_ger) {
      formIsValid = false;
      errors["imprintDescription_ger"] = "*Bitte Beschreibung eingeben (DE)";
    }
    if (!editorState?.content) {
      formIsValid = false;
      errors["imprintDescription"] = "*Bitte Beschreibung eingeben (EN)";
    }
    setErrors(errors);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const buildPayload = () => ({
    imprintName: imprintData?.imprintName,
    imprintName_ger: imprintData?.imprintName_ger,
    imprintDescription: editorState?.content,
    imprintDescription_ger: editorState?.content_ger,
  });

  //for get all imprint
  const getAllImprintData = async () => {
    setLoaderForGetAll(true);
    const searchParam = search ? `&letter=${search}` : "";
    await ApiGet(`imprint/find?page=${page}&limit=${countPerPage}${searchParam}`)
      .then((res: any) => {
        setGetAllImprint(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch((err: any) => {
        setLoaderForGetAll(false);
      });
  };

  //for Add Imprint
  const addImprintData = async (e: any) => {
    setIsEditApi(false);
    if (formValidation()) {
      setLoader(true);

      await ApiPost(`imprint/create`, buildPayload())
        .then((res: any) => {
          toast.success("Impressum erfolgreich erstellen");
          setAddimprint(false);
          setIsEditApi(false);
          getAllImprintData();
          setImprintData({});
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
          setLoader(false);
        });
    }
  };

  //for Edit Imprint
  const editImprintData = async (e: any) => {
    if (formValidation()) {
      setLoader(true);

      await ApiPut(`imprint/update?id=${imprintIdForEdit}`, buildPayload())
        .then((res: any) => {
          toast.success("Impressum erfolgreich aktualisiert");
          getAllImprintData();
          setAddimprint(false);
          setIsEditApi(false);
          setImprintData({});
          setEditorState({ content: "", content_ger: "" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
          setLoader(false);
        });
    }
  };

  //for Delete Imprint
  const deleteImprintData = async () => {
    await ApiDelete(`imprint/delete?id=${imprintIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Impressum erfolgreich löschen ");
          setShow(false);
          setPage(1);
          getAllImprintData();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
      });
  };

  // -------------------------DATA TABLE--------------------
  // Content is sanitized via the createSanitizedMarkup utility (uses DOMPurify)
  const columns: TableColumnDef[] = [
    {
      header: "Titel",
      render: (row: any) => (lang === "de" ? row?.imprintName_ger : row?.imprintName) || "-",
      getValue: (row: any) => (lang === "de" ? row?.imprintName_ger : row?.imprintName),
    },
    {
      header: "Beschreibung",
      render: (row: any) => (
        <Box
          className="showmore-class"
          sx={{ p: 2 }}
          dangerouslySetInnerHTML={createSanitizedMarkup({
            html: lang === "de" ? row?.imprintDescription_ger : row?.imprintDescription,
          })}
        />
      ),
    },
    {
      header: "Datum",
      render: (row: any) => moment(row?.createdAt).format("DD.MM.YYYY"),
      getValue: (row: any) => row?.createdAt,
    },
    {
      header: "Einstellungen",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {canEdit && (
            <Tooltip title="Impressum bearbeiten" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setIsEditApi(true);
                  setAddimprint(true);
                  setImprintIdForEdit(row?._id);
                  setImprintData({
                    imprintName: row?.imprintName,
                    imprintName_ger: row?.imprintName_ger,
                  });
                  setEditorState({
                    content: row?.imprintDescription || "",
                    content_ger: row?.imprintDescription_ger || "",
                  });
                }}
              >
                <CreateIcon />
              </IconButton>
            </Tooltip>
          )}

          {canDelete && (
            <Tooltip title="Impressum löschen" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setImprintIdForDelete(row?._id);
                  setShow(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="Impressum"
            onSearch={(val) => { setSearch(val); setPage(1); }}
            onAdd={canCreate ? () => setAddimprint(true) : undefined}
          />

          <LanguageTabs value={lang} onChange={setLang} />
        </Box>
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllImprint || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>
      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie dieses Impressum löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Nein
          </Button>
          <Button onClick={() => deleteImprintData()} color="error" variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      {addImprint === true ? (
        <Dialog fullScreen open={addImprint}>
          <Toolbar>
            <IconButton
              edge="end"
              color="inherit"
              onClick={(e) => addImpreintModelClose(e)}
              aria-label="close"
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Deutsch</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>English</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Titel</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="imprintName_ger"
                    value={imprintData?.imprintName_ger || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors?.imprintName_ger}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="imprintName"
                    value={imprintData?.imprintName || ""}
                    onChange={onhandleChange}
                  />
                  <Typography variant="caption" color="error">{errors?.imprintName}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Beschreibung</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content_ger: editor?.getContent() }));
                    }}
                    initialValue={editorState.content_ger}
                  />
                  <Typography variant="caption" color="error">{errors?.imprintDescription_ger}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content: editor?.getContent() }));
                    }}
                    initialValue={editorState.content}
                  />
                  <Typography variant="caption" color="error">{errors?.imprintDescription}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Button
                variant="contained"
                sx={{ mr: 2 }}
                disabled={loader}
                onClick={(e) => { isEditApi ? editImprintData(e) : addImprintData(e); }}
              >
                {isEditApi ? "Speichern" : "Hinzufügen"}
                {loader && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Button>
            </Box>
          </List>
        </Dialog>
      ) : null}
    </>
  );
}
